import React, { useMemo, memo } from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';

/* ─────────────────── animation presets ─────────────────── */
const cardVariants = {
  hidden: { opacity: 0, y: 60, scale: 0.95 },
  visible: (i = 0) => ({
    opacity: 1, y: 0, scale: 1,
    transition: { delay: i * 0.2, duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] as const }
  }),
  hover: {
    y: -12, scale: 1.02, rotateX: 5,
    transition: { duration: 0.3, ease: 'easeOut' }
  }
};

const bubbleFloat = {
  y: [0, -10, 0],
  x: [0, -2, 3, 0],
  rotate: [0, -0.6, 0.4, 0],
  transition: { duration: 9, ease: 'easeInOut' as const, repeat: Infinity, repeatType: 'reverse' as const }
};

/* ─────────── styled components ─────────── */
const StepCardWrapper = styled(motion.div)`
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 520px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
  cursor: default;
  transform-style: preserve-3d;

  /* 👇 compositing hints */
  will-change: transform, opacity;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  contain: layout paint style;

  &:hover { box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15); }
`;

const RequestCardStyled = styled(StepCardWrapper)`
  background: linear-gradient(135deg, #8c54ff 0%, #3d93ff 50%, #00d1b2 100%);
  position: relative;
`;

/* wrapper that carries the infinite float so it doesn't collide with variants */
const FloatLayer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  will-change: transform;
`;

const CardContent = styled.div`
  padding: 1.75rem 2rem 2rem;
  margin-top: auto;
  color: #fff;
  /* backdrop-filter: blur(8px); */ /* ← optionally re-enable if desired */
  background: rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 2;
`;

const StepTitle = styled.h3`
  font-size: 2rem;
  margin: 0 0 1rem;
  font-weight: 600;
`;

const StepDescription = styled.p`
  font-size: 1.125rem;
  margin: 0;
  line-height: 1.5;
`;

const CardTop = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  flex-direction: column;
`;

const CategoryContainer = styled.div`
  position: absolute;
  inset: 0;                 /* fill the whole card */
  overflow: hidden;
  z-index: 1;
  pointer-events: none;     /* so chips don't block hover/clicks */
`;

const CategoryTrack = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;         /* ← fix invalid auto% */
  height: 100%;
`;

const Category = styled(motion.div)`
  position: absolute;  /* ← so 'top' works */
  left: 0;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.3);
  padding: 0.5rem 1.5rem;
  border-radius: 50px;
  font-size: 0.9rem;
  color: #fff;
  backdrop-filter: blur(2px);
  will-change: transform;
  font-size: 0.84rem;
`;

/* ─────────── types ─────────── */
interface RequestCardProps {
  customIndex?: number;
  className?: string;
}

/* ─────────── stable data (module scope) ─────────── */
const CATEGORIES = [
  'Logos', 'Social Media', 'Email', 'Mobile apps', 'Packaging',
  'Landing pages', 'Ad creatives', 'User interface', 'Webflow', 'Branding',
  'Print Design', 'Illustrations', 'Animation', 'Photography', 'Video Editing'
] as const;

const SECOND_CATEGORIES = [
  'Brand Identity', 'Web Design', 'App Design', 'Marketing', 'Typography',
  'Icon Design', 'Presentations', 'Infographics', 'Book Design', 'Merchandise'
] as const;

/* simple deterministic RNG so values don't change on re-renders/SSR */
function mulberry32(seed: number) {
  return function () {
    let t = (seed += 0x6D2B79F5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ─────────── component ─────────── */
function RequestCardBase({ customIndex = 0, className }: RequestCardProps) {
  const reduceMotion = useReducedMotion();

  const animSet1 = useMemo(() => {
    const rng = mulberry32(1337);
    return CATEGORIES.map(() => ({
      topPct: +(rng() * 85).toFixed(2), 
      delay: +(rng() * 10).toFixed(2),
      duration: +(8 + rng() * 6).toFixed(2),
    }));
  }, []); // compute once

  const animSet2 = useMemo(() => {
    const rng = mulberry32(7331);
    return SECOND_CATEGORIES.map(() => ({
      top: Math.round(rng() * 160),
      delay: +(5 + rng() * 10).toFixed(2),
      duration: +(8 + rng() * 6).toFixed(2),
    }));
  }, []);

  return (
    <RequestCardStyled
      className={className}
      custom={customIndex}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      whileHover="hover"
      viewport={{ once: true, amount: 0.2 }}
    >
      <FloatLayer animate={reduceMotion ? undefined : bubbleFloat}>
        <CardTop>
          <CategoryContainer>
            <CategoryTrack>
              {CATEGORIES.map((category, index) => {
                const cfg = animSet1[index];
                return (
                  <Category
                    key={`${category}-${index}`}
                    style={{ top: `${cfg.topPct}%` }}
                    initial={{ x: '120%', opacity: 0 }}
                    animate={{ x: ['120%', '-120%'], opacity: [0, 1, 1, 0] }}
                    transition={{
                      duration: cfg.duration,
                      delay: cfg.delay,
                      repeat: Infinity,
                      ease: 'linear',
                      repeatType: 'loop',
                    }}
                    whileHover={{ scale: 1.1, transition: { duration: 0.2 } }}
                  >
                    {category}
                  </Category>
                );
              })}
              {SECOND_CATEGORIES.map((category, index) => {
                const cfg = animSet2[index];
                return (
                  <Category
                    key={`${category}-second-${index}`}
                    style={{ top: cfg.top }}
                    initial={{ x: '120%', opacity: 0 }}
                    animate={{ x: ['120%', '-120%'], opacity: [0, 1, 1, 0] }}
                    transition={{
                      duration: cfg.duration,
                      delay: cfg.delay,
                      repeat: Infinity,
                      ease: 'linear',
                      repeatType: 'loop',
                    }}
                    whileHover={{ scale: 1.1, transition: { duration: 0.2 } }}
                  >
                    {category}
                  </Category>
                );
              })}
            </CategoryTrack>
          </CategoryContainer>
        </CardTop>

        <CardContent>
          <StepTitle>Request</StepTitle>
          <StepDescription>
            Request whatever you'd like in our internal ticketing system. Stack up as many requests as you like.
          </StepDescription>
        </CardContent>
      </FloatLayer>
    </RequestCardStyled>
  );
}

/* ─────────── memoization ─────────── */
function propsAreEqual(prev: Readonly<RequestCardProps>, next: Readonly<RequestCardProps>) {
  // Re-render only if these change. Add more props here if you later extend this component.
  return prev.customIndex === next.customIndex && prev.className === next.className;
}

const RequestCard = memo(RequestCardBase, propsAreEqual);
RequestCard.displayName = 'RequestCard';

export default RequestCard;
